"use client";

import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

function ServicesSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 skeleton-card min-h-[260px]">
        <div className="skeleton skeleton-icon" />
        <div className="skeleton skeleton-line skeleton-line-medium mb-4" style={{ height: '22px' }} />
        <div className="skeleton skeleton-line skeleton-line-full" />
        <div className="skeleton skeleton-line skeleton-line-full" />
        <div className="skeleton skeleton-line skeleton-line-medium" />
      </div>
      {[1, 2, 3, 4]?.map((i) => (
        <div key={i} className="skeleton-card min-h-[200px]">
          <div className="skeleton skeleton-icon" />
          <div className="skeleton skeleton-line skeleton-line-medium mb-3" style={{ height: '18px' }} />
          <div className="skeleton skeleton-line skeleton-line-full" />
          <div className="skeleton skeleton-line skeleton-line-medium" />
        </div>
      ))}
    </div>
  );
}

// Parallax depths per card index (px offset at full scroll range)
const CARD_PARALLAX_DEPTHS = [0, -30, -55, -40, -65, -50];

function ParallaxCard({
  children,
  depth,
  className,
}: {
  children: React.ReactNode;
  depth: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [Math.abs(depth) * 0.5, depth]);

  return (
    <motion.div ref={ref} style={{ y }} className={className}>
      {children}
    </motion.div>
  );
}

export default function ServicesSection() {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.08 });
  const [loaded, setLoaded] = useState(false);

  const services = [
    {
      icon: (
        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
        </svg>
      ),
      title: t('service1_title'),
      desc: t('service1_desc'),
      highlight: t('services_popular'),
    },
    {
      icon: (
        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
        </svg>
      ),
      title: t('service2_title'),
      desc: t('service2_desc'),
    },
    {
      icon: (
        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
      ),
      title: t('service3_title'),
      desc: t('service3_desc'),
    },
    {
      icon: (
        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
        </svg>
      ),
      title: t('service4_title'),
      desc: t('service4_desc'),
    },
    {
      icon: (
        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
        </svg>
      ),
      title: t('service5_title'),
      desc: t('service5_desc'),
    },
  ];

  useEffect(() => {
    const loadTimer = setTimeout(() => setLoaded(true), 600);
    return () => clearTimeout(loadTimer);
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-28 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-px bg-gradient-to-r from-transparent via-[rgba(255,145,46,0.3)] to-transparent" />
      <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,145,46,0.04) 0%, transparent 70%)', transform: 'translate(-50%,-50%)' }} />
      <div className="max-w-container mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          custom={0}
          className="max-w-2xl mb-12 sm:mb-16"
        >
          <div className="section-label mb-4">{t('services_label')}</div>
          <h2 className="font-heading text-headline font-bold text-white mb-5">
            {t('services_headline_1')}{' '}
            <span className="text-gradient-orange">{t('services_headline_accent')}</span>{' '}
            {t('services_headline_2')}
          </h2>
          <p className="text-muted text-base sm:text-lg leading-relaxed">{t('services_sub')}</p>
        </motion.div>

        {!loaded ? (
          <ServicesSkeleton />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Large featured card — subtle parallax */}
            <ParallaxCard depth={CARD_PARALLAX_DEPTHS[0]} className="lg:col-span-2 lg:row-span-1">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate={inView ? 'visible' : 'hidden'}
                custom={1}
              >
                <div className="card-glass card-glow-hover rounded-3xl p-6 sm:p-8 h-full min-h-[240px] sm:min-h-[260px] relative overflow-hidden group cursor-pointer border border-[rgba(139,158,192,0.15)]">
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: 'radial-gradient(circle at 30% 50%, rgba(255,145,46,0.1) 0%, transparent 65%)' }}
                  />
                  <div className="absolute top-5 right-5 sm:top-6 sm:right-6 px-3 py-1 rounded-full bg-[rgba(255,145,46,0.15)] border border-[rgba(255,145,46,0.3)]">
                    <span className="text-[10px] font-bold text-primary uppercase tracking-widest">{services[0].highlight}</span>
                  </div>
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-xl bg-[rgba(255,145,46,0.12)] border border-[rgba(255,145,46,0.2)] flex items-center justify-center text-primary mb-6 group-hover:bg-[rgba(255,145,46,0.22)] group-hover:border-[rgba(255,145,46,0.45)] group-hover:shadow-[0_0_20px_rgba(255,145,46,0.35)] transition-all duration-400">
                      {services[0].icon}
                    </div>
                    <h3 className="font-heading text-xl sm:text-2xl font-bold text-white mb-3">{services[0].title}</h3>
                    <p className="text-muted text-sm sm:text-base leading-relaxed max-w-md">{services[0].desc}</p>
                    <div className="mt-6 flex items-center gap-2 text-primary text-sm font-semibold group-hover:gap-4 transition-all duration-300">
                      <span>{t('services_learn_more')}</span>
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </motion.div>
            </ParallaxCard>

            {/* Regular cards — each with a different parallax depth */}
            {services.slice(1).map((service, i) => (
              <ParallaxCard key={service.title} depth={CARD_PARALLAX_DEPTHS[i + 1]}>
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  animate={inView ? 'visible' : 'hidden'}
                  custom={i + 2}
                >
                  <div className="card-glass card-glow-hover rounded-3xl p-6 sm:p-7 h-full min-h-[190px] sm:min-h-[200px] relative overflow-hidden group cursor-pointer border border-[rgba(139,158,192,0.15)]">
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{ background: 'radial-gradient(circle at 20% 30%, rgba(255,145,46,0.08) 0%, transparent 65%)' }}
                    />
                    <div className="relative z-10">
                      <div className="w-11 h-11 rounded-xl bg-[rgba(255,145,46,0.1)] border border-[rgba(255,145,46,0.15)] flex items-center justify-center text-primary mb-5 group-hover:bg-[rgba(255,145,46,0.2)] group-hover:border-[rgba(255,145,46,0.4)] group-hover:shadow-[0_0_16px_rgba(255,145,46,0.3)] transition-all duration-400">
                        {service.icon}
                      </div>
                      <h3 className="font-heading text-lg sm:text-xl font-bold text-white mb-2">{service.title}</h3>
                      <p className="text-muted text-sm leading-relaxed">{service.desc}</p>
                    </div>
                  </div>
                </motion.div>
              </ParallaxCard>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}